import React from "react";

import {
  useRetrieveCurrentPlanzone,
  useZonesRetrieval,
} from "@planzone/view-models";
import { Notifications } from "@scenes/notifications";
import { AssistantShortcut } from "@scenes/assistantShortcut";
import { ProfileNavigation } from "@common/profileNavigation";
import { WorkspaceContext } from "@scenes/workspaceContext";
import { Loader } from "@common/loader";
import { Props } from "./types";
import { Calendar } from "@scenes/calendar";
import { useCalendarData } from "@scenes/calendar/useCalendarData";
import { SideBar } from "./sideBar";
import styles from "./styles.scss";
import { usePreferences } from "@components/preferences";
import { Project } from "@planzone/dto";

function CalendarPage(_: Props) {
  const { state, edit } = usePreferences();
  const { calendarFilter } = state;

  const { planzone } = useRetrieveCurrentPlanzone();

  const {
    zones,
    isRequestPending: isZonesRequestPending,
    isRequestSuccess: isZonesRequestSuccess,
    isRequestFailure: isZonesRequestFailure,
    retrieveZones,
  } = useZonesRetrieval(planzone);

  const allProjects = React.useMemo(
    () => zones.flatMap(({ projects }) => projects),
    [zones],
  );

  const selectedProjectsUuids = (calendarFilter.projects || []).map(
    (p) => p.uuid,
  );

  const toggleProjectFilter = React.useCallback(
    (projectUuid: string) => {
      const currentProjects = calendarFilter.projects || [];
      const isSelected = currentProjects.some((p) => p.uuid === projectUuid);

      const newProjects = isSelected
        ? currentProjects.filter((p) => p.uuid !== projectUuid)
        : ([
            ...currentProjects,
            allProjects.find((p) => p.uuid === projectUuid),
          ].filter(Boolean) as Project[]);

      edit({
        ...state,
        calendarFilter: {
          ...calendarFilter,
          projects: newProjects,
        },
      });
    },
    [calendarFilter, allProjects, state, edit],
  );

  const {
    cursor,
    setCursor,
    hasPlanzone,
    isRequestPending,
    events,
    tasks,
    calendar,
    weeks,
    currentWeek,
  } = useCalendarData(calendarFilter);

  React.useEffect(() => {
    !isZonesRequestPending &&
      !isZonesRequestSuccess &&
      !isZonesRequestFailure &&
      retrieveZones(planzone);
  }, [
    planzone.uuid,
    isZonesRequestPending,
    isZonesRequestSuccess,
    retrieveZones,
  ]);

  const isLoading = isRequestPending && isZonesRequestPending;

  return (
    <WorkspaceContext type="planzone">
      <div className={styles.calendarPage}>
        <div className={styles.header}>
          <div className={styles.profileNavigation}>
            <AssistantShortcut />
            <Notifications />
            <ProfileNavigation />
          </div>
        </div>
        <div className={styles.main}>
          <SideBar
            projects={allProjects}
            isRequestPending={isZonesRequestPending}
            selectedProjects={selectedProjectsUuids}
            onToggleProject={toggleProjectFilter}
          />
          {isLoading ? (
            <div className={styles.loader}>
              <div className={styles.loaderMain}>
                <Loader />
              </div>
            </div>
          ) : (
            <div className={styles.calendar}>
              <Calendar
                date={cursor}
                events={events}
                milestones={calendar.milestones}
                tasks={tasks}
                editDate={setCursor}
                zones={zones}
                isRequestPending={isRequestPending}
                month={weeks}
                currentWeek={currentWeek}
                renderDay={(date, events) => (
                  <div>
                    {events.map((event) => (
                      <div key={event.uuid} className={styles.event}>
                        <p>{event.name}</p>
                      </div>
                    ))}
                  </div>
                )}
                planzone={planzone}
                hasPlanzone={hasPlanzone}
              />
            </div>
          )}
        </div>
      </div>
    </WorkspaceContext>
  );
}

export { CalendarPage };
